import { useState, useEffect, useRef } from 'react'

interface ElapsedTimerOptions {
  /** Server-provided effective duration (TimeSpan string "HH:MM:SS" or "00:00:00") */
  effectiveDuration: string | null
  /** Whether the session is currently active (timer should run) */
  isActive: boolean
  /** Whether the adventure clock is currently paused */
  isTimerPaused?: boolean
  /** Update interval in ms (default: 1000) */
  interval?: number
}

interface ElapsedTimerResult {
  /** Total elapsed seconds (the adventure clock value) */
  elapsedSeconds: number
  /** Formatted time string (MM:SS or HH:MM:SS) */
  formattedTime: string
}

/**
 * Parse a TimeSpan string ("HH:MM:SS" or "d.HH:MM:SS" or "HH:MM:SS.fffffff") to total seconds
 */
export function parseTimeSpan(timeSpan: string): number {
  if (!timeSpan) return 0

  // Remove fractional seconds if present
  const cleaned = timeSpan.split('.').length > 2
    ? timeSpan // "d.HH:MM:SS.fff" format
    : timeSpan

  // Handle "d.HH:MM:SS" format (days prefix)
  let days = 0
  let timePart = cleaned
  const dotParts = cleaned.split('.')
  if (dotParts.length >= 2 && dotParts[0].indexOf(':') === -1) {
    days = parseInt(dotParts[0], 10) || 0
    // Rejoin remaining parts (could be "HH:MM:SS.fffffff")
    timePart = dotParts.slice(1).join('.')
  }

  // Remove fractional seconds
  const colonPart = timePart.split('.')[0]
  const parts = colonPart.split(':')

  if (parts.length === 3) {
    const hours = parseInt(parts[0], 10) || 0
    const minutes = parseInt(parts[1], 10) || 0
    const seconds = parseInt(parts[2], 10) || 0
    return days * 86400 + hours * 3600 + minutes * 60 + seconds
  } else if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10) || 0
    const seconds = parseInt(parts[1], 10) || 0
    return days * 86400 + minutes * 60 + seconds
  }

  return 0
}

/**
 * Format seconds to MM:SS or HH:MM:SS
 */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }

  const totalSeconds = Math.floor(seconds)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Hook to track elapsed time for an adventure session.
 * Uses the server's effectiveDuration as the base and ticks forward client-side.
 * When the server provides an updated effectiveDuration (via polling), it re-syncs.
 */
export function useElapsedTimer({
  effectiveDuration,
  isActive,
  isTimerPaused = false,
  interval = 1000,
}: ElapsedTimerOptions): ElapsedTimerResult {
  // Parse server's effective duration to seconds
  const serverSeconds = effectiveDuration ? parseTimeSpan(effectiveDuration) : 0

  // Track the server value and when we received it, so we can tick forward
  const [displaySeconds, setDisplaySeconds] = useState(serverSeconds)
  const lastServerSecondsRef = useRef(serverSeconds)
  const lastSyncTimeRef = useRef(Date.now())

  // Re-sync when server provides a new effectiveDuration
  useEffect(() => {
    if (serverSeconds !== lastServerSecondsRef.current) {
      lastServerSecondsRef.current = serverSeconds
      lastSyncTimeRef.current = Date.now()
      setDisplaySeconds(serverSeconds)
    }
  }, [serverSeconds])

  // Tick forward client-side while active and not paused
  useEffect(() => {
    if (!isActive || isTimerPaused) return

    const timer = setInterval(() => {
      const sinceSyncMs = Date.now() - lastSyncTimeRef.current
      const sinceSyncSeconds = Math.floor(sinceSyncMs / 1000)
      setDisplaySeconds(lastServerSecondsRef.current + sinceSyncSeconds)
    }, interval)

    return () => clearInterval(timer)
  }, [isActive, isTimerPaused, interval])

  // When paused, just show the server value (don't tick)
  useEffect(() => {
    if (isTimerPaused) {
      setDisplaySeconds(lastServerSecondsRef.current)
    } else if (isActive) {
      // Resuming - reset sync point
      lastSyncTimeRef.current = Date.now()
    }
  }, [isTimerPaused, isActive])

  const formattedTime = formatTime(displaySeconds)

  return {
    elapsedSeconds: displaySeconds,
    formattedTime,
  }
}
